import React, { useEffect, useMemo, useState } from 'react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { RequestAbsenceButtonFragment } from '../../__generated__/graphql';
import RequestAbsenceSidebar from '../RequestAbsenceSidebar';
import { mapEmployeeAbsenceCategoryToAbsenceCategory } from '../RequestAbsenceSidebar/mappings';
import { PureQueryOptions } from '@apollo/client';

type EmployeeAbsenceCategoryProps = RequestAbsenceButtonFragment;

type Props = Pick<ButtonProps, 'variant' | 'size'> & {
  categoryId: string | null;
  categories: ReadonlyArray<EmployeeAbsenceCategoryProps>;
  buttonLabel?: string;
  block?: boolean;
  button?: React.ReactNode;
  isProcessing?: boolean;
  initialOpen?: boolean;
  onSuccess?: () => void;
  refetchQueries?: PureQueryOptions[];
  onSidebarOpenChange?: (isOpen: boolean) => void;
};

const RequestAbsenceButton: React.FC<React.PropsWithChildren<Props>> = ({
  categoryId,
  categories,
  buttonLabel,
  block,
  button,
  isProcessing = false,
  initialOpen = false,
  onSuccess,
  refetchQueries,
  onSidebarOpenChange,
  ...rest
}) => {
  const { t } = useTranslation();
  const [show, setShow] = useState<boolean>(initialOpen);

  useEffect(() => {
    if (initialOpen) {
      setShow(true);
      onSidebarOpenChange?.(true);
    }
  }, [initialOpen, onSidebarOpenChange]);

  const handleClose = () => {
    setShow(false);
    onSidebarOpenChange?.(false);
  };

  const handleShow = () => {
    setShow(true);
    onSidebarOpenChange?.(true);
  };

  const absenceCategories = useMemo(
    () => categories.map(mapEmployeeAbsenceCategoryToAbsenceCategory),
    [categories],
  );

  let requestBtn = (
    <Button onClick={handleShow} {...rest} disabled={isProcessing}>
      {buttonLabel ?? t('absences.cards.absenceCategories.requestAbsence')}
    </Button>
  );

  if (button) {
    requestBtn = React.cloneElement(button as React.ReactElement, {
      onClick: handleShow,
      isProcessing,
    });
  }

  return (
    <div className={block ? 'd-grid' : undefined}>
      {requestBtn}
      <RequestAbsenceSidebar
        absenceCategoryId={categoryId ?? absenceCategories[0]?.id ?? ''}
        show={show}
        onClose={handleClose}
        absenceCategories={absenceCategories}
        onSuccess={onSuccess}
        refetchQueries={refetchQueries}
      />
    </div>
  );
};

export default RequestAbsenceButton;
